import React, { useState } from 'react'
import GenericModal from '../../../../components/GenericModal'
import Dropdown from '../../../../components/Dropdown'
import { CreateAndEditTermsSection } from '../CreateAndEditTermsSection'
import CsvMappingUpload from '../../../../components/CsvMappingUpload'
import { getTermsSectionsApi } from '../../../../store/slices/termsSectionsSlice/api'
import { useAppDispatch } from '../../../../store'
import api from '../../../../utils/api'
import { exportCsv } from '../../../../utils/exportCsv'
import { notification } from '../../../../components/Notifications'
import { useParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { resetCourseSectionsByTermId } from '../../../../APIManager/TermsManager'
import { type ApiError } from '../../../../models/Common'
import { resetInstructorsByTermId } from '../../../../APIManager/UserManager'

interface TermsTopbarActionsProps {
  params: Record<string, any>
}

type ResetTarget = 'sections' | 'instructors' | null

const resetMeta = {
  sections: {
    title: 'Reset Course Sections',
    body: 'Are you sure you want to reset all course sections for this term? This action will permanently remove all unlinked sections and cannot be undone.',
    tooltip: 'This will remove all course sections from this term. Use this if you want to start over or re‑import enrollments. This cannot be undone.',
    successMsg: 'Course sections successfully reset',
    api: async (termId: string) => await resetCourseSectionsByTermId(termId)
  },
  instructors: {
    title: 'Reset Instructor Assignments',
    body: 'Are you sure you want to reset all instructor assignments for this term? This action will permanently remove all existing assignments unlinked to a project and cannot be undone.',
    tooltip: 'This will remove all instructor assignments from this term. Use this if you want to start over or re‑import instructor assignments. This cannot be undone.',
    successMsg: 'Instructor assignments successfully reset',
    api: async (termId: string) => await resetInstructorsByTermId(termId)
  }
} as const

const TermsTopbarActions: React.FC<TermsTopbarActionsProps> = ({ params }) => {
  const [isUploadModal, setIsUploadModal] = useState<boolean>(false)
  const [isUploadRolesModal, setIsUploadRolesModal] = useState<boolean>(false)
  const [isCreateModal, setIsCreateModal] = useState<boolean>(false)
  const [loadingExport, setLoadingExport] = useState(false)
  const dispatch = useAppDispatch()
  const { id } = useParams()
  const closeUploadModal = (): void => {
    setIsUploadModal(false)
  }

  const closeUploadRolesModal = (): void => {
    setIsUploadRolesModal(false)
  }

  const uploadApi = (): void => {
    setIsUploadModal(true)
  }

  const handleUploadRolesModal = (): void => {
    setIsUploadRolesModal(true)
  }

  const addManually = (): void => {
    setIsCreateModal(true)
  }

  const handleCourseExport = async (): Promise<void> => {
    setLoadingExport(true)
    const apiParams = { termId: id, search: params?.search, sorting: params?.sort }
    try {
      const response = await api.get('/api/sections/export/', { params: apiParams })
      const data = response?.data

      if (data) {
        exportCsv({ data, headers: response?.headers })
      }
    } catch (error: any) {
      notification(error?.message ?? 'Internal server error', { variant: 'error' })
    } finally {
      setLoadingExport(false)
    }
  }

  const handleInstructorExport = async (): Promise<void> => {
    setLoadingExport(true)
    const params = { termId: id, isInstructor: true }
    try {
      const response = await api.get('/api/users/roles/export/', { params })
      const data = response?.data

      if (data) {
        exportCsv({ data, headers: response?.headers })
      }
    } catch (error: any) {
      notification(error?.message ?? 'Internal server error', { variant: 'error' })
    } finally {
      setLoadingExport(false)
    }
  }
  const menuItems = [
    { text: 'Upload Course Sections (.csv)', action: uploadApi },
    { text: 'Upload Instructor Assignments (.csv)', action: handleUploadRolesModal },
    { text: 'Add Manually', action: addManually }
  ]

  const exportMenuItems = [
    { text: 'Export Course Sections (.csv)', action: handleCourseExport, disabled: loadingExport },
    { text: 'Export Instructor Assignments (.csv)', action: handleInstructorExport, disabled: loadingExport }
  ]

  const [resetTarget, setResetTarget] = useState<ResetTarget>(null)
  const { mutate: resetMutation, isPending: isResetLoading } = useMutation({
    mutationFn: async (target: NonNullable<ResetTarget>) => {
      return await resetMeta[target].api(id ?? '')
    },
    onSuccess: (_, target) => {
      notification(resetMeta[target].successMsg, { variant: 'success' })
      void dispatch(getTermsSectionsApi({ params }))
    },
    onError: (error: ApiError) => {
      notification(error.message || 'Failed to reset course sections.', { variant: 'error' })
    }
  })

  const handleConfirmReset = async (): Promise<void> => {
    if (!resetTarget) return
    resetMutation(resetTarget)
    setResetTarget(null)
  }

  const resetMenuItems = [
    {
      text: 'Reset Course Sections',
      action: () => { setResetTarget('sections') },
      tooltipTitle: resetMeta.sections.tooltip
    },
    {
      text: 'Reset Instructor Assignments',
      action: () => { setResetTarget('instructors') },
      tooltipTitle: resetMeta.instructors.tooltip
    }
  ]

  const activeMeta = resetTarget ? resetMeta[resetTarget] : null

  return (
    <>
      <Dropdown menuItems={exportMenuItems} label='Exports' extraButtonProps={{ loading: loadingExport, disabled: loadingExport }} />
      <Dropdown menuItems={menuItems} label='Add Course Sections' />
      <GenericModal
        isOpen={isCreateModal}
        title={'Create Course Section'}
        onClose={() => { setIsCreateModal(false) }}
        maxWidth='sm'
        action={false}
      >
        <CreateAndEditTermsSection params={params} handleCloseModal={() => { setIsCreateModal(false) }} />
      </GenericModal>

      <CsvMappingUpload isOpen={isUploadModal} onClose={closeUploadModal} modalTitle="Course Sections" csvHeaderEndpoint="/api/sections/csv_headers" uploadCsvEndpoint={`/api/sections/?term_id=${params?.termId}`} onSuccess={() => {
        notification('Course sections successfully uploaded', { variant: 'success' })
        void dispatch(getTermsSectionsApi({ params }))
      }} />

      <CsvMappingUpload isOpen={isUploadRolesModal} onClose={closeUploadRolesModal} modalTitle="Instructors" csvHeaderEndpoint="/api/users/roles/csv_headers" uploadCsvEndpoint={`/api/users/roles/?is_faculty=True&term_id=${params?.termId}`} onSuccess={() => {
        notification('Instructor assignments successfully uploaded', { variant: 'success' })
        void dispatch(getTermsSectionsApi({ params }))
      }
      } />

      <Dropdown menuItems={resetMenuItems} label='Reset Course Sections' />
      <GenericModal
        isOpen={!!resetTarget}
        onClose={() => { setResetTarget(null) }}
        title={activeMeta?.title ?? ''}
        maxWidth='sm'
        onConfirm={handleConfirmReset}
        confirmButtonLoading={isResetLoading}
      >
        {activeMeta?.body ?? ''}
      </GenericModal>
    </>
  )
}

export default TermsTopbarActions
